import React, {useState, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {Redirect, useHistory} from 'react-router-dom'
import {
    Box,
    Stack,
    Button,
    Spinner,
    useMultiStyleConfig,
    SimpleGrid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AbsoluteCenter, useBreakpointValue, Alert, AlertDescription} from '@chakra-ui/react'
import {useCustomer, useCustomerId, useAccessToken} from '@salesforce/commerce-sdk-react'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import Field from '../../components/field'
import useOrderStatusFields from '../../components/forms/useOrderStatusFields'
import {useGlobalOrderTracking} from '../../contexts/global-elements-context'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {
    OrderInfo,
    OrderShippingAddress,
    OrderProducts,
    OrderPayment,
    ManageOrder,
    OrderDetailsSummary,
    ReturnAlert,
    ReturnOrderPolicyAlert,
    OrderProductStatus
} from '../account/partials'
import {DSSActions} from '../account/partials/dss-actions'
import {ORDER_RAW_STATUS, ORDER_PRODUCTS_RENDER_MODES, ORDER_RETURN_RAW_STATUS} from '../account/constant'

const OrderTracking = () => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('OrderTracking')
    const customerId = useCustomerId()
    const {data: customer, isLoading: isCustomerLoading} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId}
    )
    const {data: orderTrackingContent} = useGlobalOrderTracking()
    const history = useHistory()
    const {site} = useMultiSite()
    const [isLoading, setIsLoading] = useState(false)
    const [orderData, setOrderData] = useState(null)
    const [serverError, setServerError] = useState(null)
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const config = getConfig()
    const {currency} = useCurrency()
    const isMobile = useBreakpointValue({base: true, lg: false})

    const form = useForm({
        mode: 'onBlur',
        defaultValues: {
            orderNumber: '',
            orderEmail: '',
            billingPostcode: ''
        }
    })

    const {handleSubmit} = form
    const fields = useOrderStatusFields({form})

    const lookupGuestOrder = useCallback(async (data) => {
        setIsLoading(true)
        setServerError(null)

        try {
            const token = await getTokenWhenReady()
            const {proxyPath, parameters} = config.app.commerceAPI
            const organizationId = parameters.organizationId

            const url = `${appOrigin}${proxyPath}/custom/order-details-guest/v1/organizations/${organizationId}/order-details-guest?siteId=${site.id}`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    orderNumber: data.orderNumber,
                    orderEmail: data.orderEmail,
                    postalCode: data.billingPostcode
                })
            })

            if (!response.ok) {
                throw new Error('Order not found')
            }

            const result = await response.json()

            if (result?.order) {
                setOrderData(result.order)
                setServerError(null)
            } else {
                setServerError(
                    intl.formatMessage({
                        defaultMessage: 'Invalid order number, email or postcode.',
                        id: 'order_tracking.error.invalid_combination'
                    })
                )
            }
        } catch (error) {
            setServerError(
                intl.formatMessage({
                    defaultMessage: 'Invalid order number, email or postcode.',
                    id: 'order_tracking.error.invalid_combination'
                })
            )
            console.error('Order lookup error:', error)
        } finally {
            setIsLoading(false)
        }
    }, [getTokenWhenReady, config, appOrigin, site.id, intl])

    const onSubmit = useCallback((data) => lookupGuestOrder(data), [lookupGuestOrder])

    // Order tracking is only available to guest users
    if (isCustomerLoading) {
        return (
            <AbsoluteCenter>
                <Spinner />
            </AbsoluteCenter>
        )
    }

    if (customer?.authType === 'registered') {
        return <Redirect to="/account" />
    }

    // If order data is loaded, display order details
    if (orderData) {
        const shouldShowTracking =
            orderData?.packages?.length > 0 &&
            ORDER_RAW_STATUS.TRACKING.includes(orderData.statusRaw?.toLowerCase())

        const getOrderProductsMode = (order) =>
            ORDER_RAW_STATUS.TRACKING.includes(order.statusRaw?.toLowerCase()) &&
            order.packages?.length > 0
                ? ORDER_PRODUCTS_RENDER_MODES.PACKAGES
                : ORDER_PRODUCTS_RENDER_MODES.ITEMS
        const mode = getOrderProductsMode(orderData)
        const orderProductsData =
            mode === ORDER_PRODUCTS_RENDER_MODES.PACKAGES ? orderData : orderData.items
        const shouldShowOrderProductStatus =
            orderData?.statusRaw?.toLowerCase() === 'canceled' ||
            Object.values(ORDER_RETURN_RAW_STATUS).includes(orderData?.statusRaw?.toLowerCase())
        const {returnable} = orderData.returnableStatus || {}

        return (
            <>
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                    body.order-details-active [data-testid="page-header"],
                    body.order-details-active [data-testid="footer"],
                    body.order-details-active [data-block-type="LandingPage"] [data-block-type]:not([data-block-type="OrderStatusFormPlaceholderBlock"]):not([data-block-type="OrderStatusFormPlaceholderBlock-OrderView"]) {
                        display: none !important;
                    }
                `}}
                />

                <Box
                    data-block-type="OrderStatusFormPlaceholderBlock-OrderView"
                    bg="white"
                    minH="100vh"
                    py={{base: 6, lg: 10}}
                    px={{base: '16px', lg: 0}}
                >
                    <Box maxW="1200px" mx="auto">
                        <Stack spacing={6}>
                            <SimpleGrid columns={{base: 1, lg: 1}} width={{base: 'full', lg: '458px'}}>
                                <Box>
                                    <Stack spacing={6}>
                                        <OrderInfo order={orderData}/>
                                        {!isMobile && returnable && (
                                            <>
                                                <ReturnAlert orderItems={orderData.items}/>
                                                <ReturnOrderPolicyAlert
                                                    {...orderData.returnableStatus}
                                                    containerProps={{mb: 6}}
                                                />
                                            </>
                                        )}
                                        {isMobile && returnable && (
                                            <ManageOrder
                                                containerProps={{mt: 2}}
                                                dividerProps={{mb: 4}}
                                                {...orderData.returnableStatus}
                                                returnLabel={orderData.returnLabel}
                                            />
                                        )}
                                        <DSSActions marginTop={6} order={orderData} />
                                        {shouldShowOrderProductStatus && (
                                            <OrderProductStatus
                                                orderBrand={orderData.orderBrand}
                                                orderProductsData={orderProductsData}
                                                currency={currency}
                                                mode={mode}
                                                shippingMethod={orderData.shippingMethod}
                                                shippingFee={orderData.shippingFee}
                                                isOnlineOrder={orderData?.isOnlineOrder}
                                                orderStatusRaw={orderData?.statusRaw}
                                            />
                                        )}
                                        <OrderShippingAddress order={orderData} />
                                        <OrderProducts
                                            orderData={orderData}
                                            orderProductsData={orderProductsData}
                                            currency={currency}
                                            mode={mode}
                                            shippingMethod={orderData?.shippingMethod}
                                            bopisStoreNumber={orderData?.bopisStoreNumber}
                                            shippingFee={orderData?.shippingFee}
                                            shippingMethodDetails={orderData?.shippingMethodDetails}
                                            storeInfo={orderData?.storeInfo}
                                            isOnlineOrder={orderData?.isOnlineOrder}
                                        />
                                        <OrderPayment order={orderData} />
                                        <OrderDetailsSummary {...orderData} currency={currency} />
                                    </Stack>
                                </Box>
                                <Box maxW={{base: 'full', lg: '370px'}}>
                                    <Box display={{base: 'none', lg: 'block'}}>
                                        <ManageOrder
                                            {...orderData.returnableStatus}
                                            returnLabel={orderData.returnLabel}
                                        />
                                    </Box>
                                </Box>
                            </SimpleGrid>
                        </Stack>
                    </Box>
                </Box>
            </>
        )
    }

    return (
        <Box data-testid="order-tracking-page" {...styles.container}>
            <Seo title="Track Order" description="Track the status of your order" />
            {orderTrackingContent?.length > 0 && (
                <Box data-testid="order-tracking-above-form">
                    {orderTrackingContent.map((entry, idx) => {
                        const expanded = entry?.ContentLink?.Expanded
                        if (!expanded) return null
                        return (
                            <React.Fragment
                                key={`${entry?.ContentLink?.Id ?? 'above-form'}-${idx}`}
                            >
                                {renderLayoutsSwitch(expanded)}
                            </React.Fragment>
                        )
                    })}
                </Box>
            )}

            {/* Server Error Banner */}
            {serverError && (
                <Alert variant="warning" status="error" mb={8}>
                    <AlertDescription flex="1">{serverError}</AlertDescription>
                </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack {...styles.fields}>
                    <Field variant="webFloatingField" {...fields.orderNumber} />
                    <Field variant="webFloatingField" {...fields.orderEmail} />
                    <Field variant="webFloatingField" {...fields.billingPostcode} />

                    <Button
                        type="submit"
                        variant="webPrimary"
                        {...styles.submitButton}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                    >
                        {intl.formatMessage({
                            defaultMessage: 'Check Status',
                            id: 'order_tracking.button.submit'
                        })}
                    </Button>
                </Stack>
            </Box>
        </Box>
    )
}

OrderTracking.getTemplateName = () => 'order-tracking'

export default OrderTracking
